import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../../app/store';
import {
  ImageMaskVisibilityValues,
  MaskColors,
} from '../../../../features/imageViewer/imageSlice';

export enum CubeMeasureEnum {
  none = '',
  detectionFactAvgCountPerMeter = 'DetectionFact.avgCountPerMeter',
  detectionFactCountStdDeviation = 'DetectionFact.countStdDeviation',
  detectionFactAvgCount = 'DetectionFact.avgCount',
  detectionFactRawCount = 'DetectionFact.rawCount',
  detectionFactCount = 'DetectionFact.count',
  detectionFactCountPerMeter = 'DetectionFact.countPerMeter',
  detectionFactEstimatedCount = 'DetectionFact.estimatedCount',
  trunkFactCount = 'TrunkFact.count',
  DetectionFactAvgArea = 'DetectionFact.avgArea',
  DetectionFactAvgLength = 'DetectionFact.avgLength',
  DetectionFactHasColorHSV = 'DetectionFact.hasColorHsv',
  detectionFactLengthStdDeviation = 'DetectionFact.lengthStdDeviation',
  detectionFactAreaStdDeviation = 'DetectionFact.areaStdDeviation',
  detectionFactImageCapturedLength = 'DetectionFact.imageCapturedLength',
  detectionFactTraveledDistance = 'DetectionFact.traveledDistance',
}

export enum CubeDimensionEnum {
  rowsideRowNum = 'Rowside.rowNum',
  rowsideViewDirection = 'Rowside.viewDirection',
  detectionFactPointId = 'DetectionFact.pointId',
  detectionFactRowsideId = 'DetectionFact.rowsideId',
  detectionFactBlockId = 'DetectionFact.blockId',
  detectionFactScanId = 'DetectionFact.scanId',
  detectionFactRanchId = 'DetectionFact.ranchId',
  detectionFactCountPerMeterInterval = 'DetectionFact.countPerMeterInterval',
  detectionFactDetections = 'DetectionFact.detections', // image masks, bbox, ai confidence scores
  trunkFactRowId = 'TrunkFact.rowId',
  rowRowNum = 'Row.rowNum',
  modelVersion = 'DetectionFact.modelVersion',
  modelName = 'DetectionFact.modelName',
  modelType = 'Model.type',
  className = 'DetectionFact.className',
  pointLocation = 'Point.location',
  pointTimestamp = 'Point.timestamp',
  pointLeftColorCorrected = 'Point.left_color_corrected',
  pointRightOrig = 'Point.right_orig',
  pointH314 = 'Point.h314',
  pointH315 = 'Point.h315',
  blockName = 'DetectionFact.blockName',
  blockId = 'Block.id',
  blockAcres = 'Block.acres',
  blockRowSpacing = 'Block.rowSpacing',
  blockPlantSpacing = 'Block.plantSpacing',
  ranchName = 'DetectionFact.ranchName',
  ranchId = 'Ranch.id',
  customerId = 'Customer.id',
  scanDate = 'DetectionFact.scanDate',
  detectionFactCustomerId = 'DetectionFact.customerId',
}

export enum CubeSegmentEnum {
  detectionFactNotNullRawCount = 'DetectionFact.not_null_raw_count',
}

export enum ChartDropdownDimensionEnum {
  // perFootByRow = 'Per Ft. by Row',
  // perMeterByRow = 'Per M. by Row',
  perPlantByRow = 'Per Plant by Row',
  perRow = 'Per Row',
  perPlantByRowside = 'Per Plant by Rowside',
  perRowside = 'Per Rowside',
  stdDeviationByRowside = 'Std. Deviation per Rowside',
  distribution = 'Distribution',
  colorDistribution = 'Color Distribution',
  fruitSize = 'Fruit Size',
}

export const ChartDropdownCustomerAvailableDimensions = [
  ChartDropdownDimensionEnum.perRowside,
  ChartDropdownDimensionEnum.distribution,
  ChartDropdownDimensionEnum.colorDistribution,
  ChartDropdownDimensionEnum.fruitSize,
];

export const chartDimensionOptions = Object.values(ChartDropdownDimensionEnum);

export interface AxesLabelsAndDropdownOptions {
  dimension: ChartDropdownDimensionEnum;
  xLabel: string;
  yLabel: string;
  dropdownLabel: string;
}

// EVERY ITEM BETWEEN THESE COMMENTS IS REQUIRED TO UPDATE TO ADD A NEW FEATURE *********************************************************************************************************************

// TODO - This logic is flawed and mixes services (currently called models and FeatureEnum) with classes (FeatureEnums).  As part of the data mesh refactor, this should be updated to use the new data registry.
// See dataRegistry.ts for a new possible structure where classes are defined as AIClass and services are defined as AIService.
export enum FeatureEnum {
  none = 'none',
  nodes = 'node',
  bud = 'bud',
  clusters = 'cluster',
  berries = 'berry',
  trunks = 'trunk',
  inflorescence = 'inflorescence',
  leaf = 'leaf',
  shoot = 'shoot',
  growth = 'growth',
  noGrowth = 'nogrowth',
  blueberry = 'blueberry',
  blueberry_Green = 'blueberry_Green',
  blueberry_Yellow = 'blueberry_Yellow',
  blueberry_Purple = 'blueberry_Purple',
  blueberry_Blue = 'blueberry_Blue',
  flower = 'flower',
  earlyShoot = 'early-shoot',
  earlyCluster = 'early-cluster',
  flower_closed = 'flower_closed',
  flower_open = 'flower_open',
  flower_petalfall = 'flower_petalfall',
}

// This is really what we are calling "Services" now and includes a few items from FeatureEnum as well.
export enum ModelNamesBesidesFeatureEnums {
  clusterBerry = 'cluster_berry',
  growthNoGrowth = 'growth_nogrowth',
  blueberry = 'blueberry',
  inflorescenceJuice = 'inflorescence_juice',
  grape_early_mid_season = 'grape_early_mid_season',
  grape_mid_season = 'grape_mid_season',
  grape_late_season = 'grape_late_season',
}

// DO NOT CHANGE ORDER, IT IS REQUIRED TO BE IN THIS ORDER FOR THE SQL QUERY!!!!!!!!!!!!!
export const BlueberryFeatures = [
  FeatureEnum.blueberry_Blue,
  FeatureEnum.blueberry_Green,
  FeatureEnum.blueberry_Purple,
  FeatureEnum.blueberry_Yellow,
];

export const BlueberryFlowerStageFeatures = [
  FeatureEnum.flower_closed,
  FeatureEnum.flower_open,
  FeatureEnum.flower_petalfall,
];

export interface AIClass {
  [CubeDimensionEnum.className]: FeatureEnum;
  [CubeDimensionEnum.modelName]: FeatureEnum | ModelNamesBesidesFeatureEnums;
  [CubeDimensionEnum.modelVersion]: string;
}

export type FeatureInfo = {
  [FeatureEnum.none]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.nodes]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.bud]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.clusters]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.berries]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.trunks]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.inflorescence]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.leaf]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.shoot]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.growth]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.noGrowth]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.blueberry]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.blueberry_Green]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.blueberry_Yellow]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.blueberry_Purple]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.blueberry_Blue]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.flower]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.earlyShoot]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.earlyCluster]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.flower_closed]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.flower_open]: AxesLabelsAndDropdownOptions[];
  [FeatureEnum.flower_petalfall]: AxesLabelsAndDropdownOptions[];
};

// List of chart dimensions for each feature, dimension dropdown will be populated with these options for each feature.  Default chart dimension will be the first option in the list.
export const FEATURE_INFO_DICTIONARY: FeatureInfo = {
  [FeatureEnum.flower]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Flowers - Rowside',
      yLabel: 'Total Estimated Flowers',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color',
      yLabel: '# per color',
      dropdownLabel: 'Flowers',
    },
  ],
  [FeatureEnum.flower_closed]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Closed Flowers - Rowside',
      yLabel: 'Total Estimated Flowers',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color',
      yLabel: '# per color',
      dropdownLabel: 'Flowers',
    },
  ],
  [FeatureEnum.flower_open]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Open Flowers - Rowside',
      yLabel: 'Total Estimated Flowers',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color',
      yLabel: '# per color',
      dropdownLabel: 'Flowers',
    },
  ],
  [FeatureEnum.flower_petalfall]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Petalfall Flowers - Rowside',
      yLabel: 'Total Estimated Flowers',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color',
      yLabel: '# per color',
      dropdownLabel: 'Flowers',
    },
  ],
  [FeatureEnum.blueberry_Green]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Berries - Rowside',
      yLabel: 'Total Estimated Berries',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
    // has custom implementation for blueberry case
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color',
      yLabel: '# per color',
      dropdownLabel: 'Berries',
    },
  ],
  [FeatureEnum.blueberry_Yellow]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Berries - Rowside',
      yLabel: 'Total Estimated Berries',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
    // has custom implementation for blueberry case
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color',
      yLabel: '# per color',
      dropdownLabel: 'Berries',
    },
  ],
  [FeatureEnum.blueberry_Purple]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Berries - Rowside',
      yLabel: 'Total Estimated Berries',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
    // has custom implementation for blueberry case
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color',
      yLabel: '# per color',
      dropdownLabel: 'Berries',
    },
  ],
  [FeatureEnum.blueberry_Blue]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Berries - Rowside',
      yLabel: 'Total Estimated Berries',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
    // has custom implementation for blueberry case
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color',
      yLabel: '# per color',
      dropdownLabel: 'Berries',
    },
  ],
  [FeatureEnum.blueberry]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Berries - Rowside',
      yLabel: 'Total Estimated Berries',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
    // has custom implementation for blueberry case
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color',
      yLabel: '# per color',
      dropdownLabel: 'Berries',
    },
    {
      dimension: ChartDropdownDimensionEnum.fruitSize,
      xLabel: '',
      yLabel: '',
      dropdownLabel: '',
    },
  ],
  [FeatureEnum.none]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Row / Rowside',
      yLabel: 'Total',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.perPlantByRowside,
      xLabel: 'Row / Rowside',
      yLabel: 'Nodes per Plant',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.stdDeviationByRowside,
      xLabel: 'Row / Rowside',
      yLabel: 'Standard Deviation',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: '',
      yLabel: '# of occurrences',
      dropdownLabel: '',
    },
  ],
  [FeatureEnum.nodes]: [
    {
      dimension: ChartDropdownDimensionEnum.stdDeviationByRowside,
      xLabel: 'Row / Rowside',
      yLabel: 'Standard Deviation',
      dropdownLabel: 'Nodes',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Dormant Buds / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Nodes',
    },
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
  ],
  [FeatureEnum.bud]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Buds / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Buds',
    },
  ],
  [FeatureEnum.clusters]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Clusters / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Clusters',
    },
    // { We decided that color distribution have no sense since we added berry model
    //   dimension: ChartDropdownDimensionEnum.colorDistribution,
    //   xLabel: 'Color (Click to hide / show)',
    //   yLabel: '# of occurrences',
    //   dropdownLabel: 'Clusters',
    // },
  ],
  [FeatureEnum.berries]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Berries / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Berries',
    },
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color (Click to hide / show)',
      yLabel: '# of occurrences',
      dropdownLabel: 'Berries',
    },
  ],
  [FeatureEnum.inflorescence]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Inflorescence / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Inflorescence',
    },
  ],
  [FeatureEnum.leaf]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Leaves / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Leaves',
    },
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color (Click to hide / show)',
      yLabel: '# of occurrences',
      dropdownLabel: 'Leaves',
    },
  ],
  [FeatureEnum.shoot]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Shoots / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Shoots',
    },
    {
      dimension: ChartDropdownDimensionEnum.colorDistribution,
      xLabel: 'Color (Click to hide / show)',
      yLabel: '# of occurrences',
      dropdownLabel: 'Shoots',
    },
  ],
  [FeatureEnum.growth]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Growth / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Growth',
    },
  ],
  [FeatureEnum.noGrowth]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'No Growth / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'No Growth',
    },
  ],
  [FeatureEnum.trunks]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.perRow,
      xLabel: 'Row',
      yLabel: 'Total Trunks',
      dropdownLabel: 'Trunks',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Row',
      yLabel: 'Total Trunks',
      dropdownLabel: 'Trunks / ft.',
    },
  ],
  [FeatureEnum.earlyCluster]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Early Clusters / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Early Clusters',
    },
  ],
  [FeatureEnum.earlyShoot]: [
    {
      dimension: ChartDropdownDimensionEnum.perRowside,
      xLabel: 'Rowside',
      yLabel: 'Total Estimated Per Rowside',
      dropdownLabel: '',
    },
    {
      dimension: ChartDropdownDimensionEnum.distribution,
      xLabel: 'Shoots / ft.',
      yLabel: '# of occurrences',
      dropdownLabel: 'Early Shoots',
    },
  ],
};

// TODO - remove this once we have a way to get the model name and version from the API
export const temporaryAIClassToModelDict: Record<FeatureEnum, AIClass> = {
  [FeatureEnum.none]: {
    [CubeDimensionEnum.className]: FeatureEnum.none,
    [CubeDimensionEnum.modelName]: FeatureEnum.none,
    [CubeDimensionEnum.modelVersion]: '',
  },
  [FeatureEnum.nodes]: {
    [CubeDimensionEnum.className]: FeatureEnum.nodes,
    [CubeDimensionEnum.modelName]: FeatureEnum.nodes,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.bud]: {
    [CubeDimensionEnum.className]: FeatureEnum.bud,
    [CubeDimensionEnum.modelName]: FeatureEnum.bud,
    [CubeDimensionEnum.modelVersion]: '1',
  },
  [FeatureEnum.clusters]: {
    [CubeDimensionEnum.className]: FeatureEnum.clusters,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.clusterBerry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.berries]: {
    [CubeDimensionEnum.className]: FeatureEnum.berries,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.clusterBerry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.trunks]: {
    [CubeDimensionEnum.className]: FeatureEnum.trunks,
    [CubeDimensionEnum.modelName]: FeatureEnum.trunks,
    [CubeDimensionEnum.modelVersion]: '4',
  },
  [FeatureEnum.inflorescence]: {
    [CubeDimensionEnum.className]: FeatureEnum.inflorescence,
    [CubeDimensionEnum.modelName]: FeatureEnum.inflorescence,
    [CubeDimensionEnum.modelVersion]: '1',
  },
  [FeatureEnum.leaf]: {
    [CubeDimensionEnum.className]: FeatureEnum.leaf,
    [CubeDimensionEnum.modelName]: FeatureEnum.leaf,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.shoot]: {
    [CubeDimensionEnum.className]: FeatureEnum.shoot,
    [CubeDimensionEnum.modelName]: FeatureEnum.shoot,
    [CubeDimensionEnum.modelVersion]: '1',
  },
  [FeatureEnum.growth]: {
    [CubeDimensionEnum.className]: FeatureEnum.growth,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.growthNoGrowth,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.noGrowth]: {
    [CubeDimensionEnum.className]: FeatureEnum.noGrowth,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.growthNoGrowth,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.blueberry_Blue]: {
    [CubeDimensionEnum.className]: FeatureEnum.blueberry_Blue,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.blueberry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.blueberry_Green]: {
    [CubeDimensionEnum.className]: FeatureEnum.blueberry_Green,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.blueberry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.blueberry_Purple]: {
    [CubeDimensionEnum.className]: FeatureEnum.blueberry_Purple,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.blueberry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.blueberry_Yellow]: {
    [CubeDimensionEnum.className]: FeatureEnum.blueberry_Yellow,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.blueberry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.blueberry]: {
    [CubeDimensionEnum.className]: FeatureEnum.blueberry,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.blueberry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.flower]: {
    [CubeDimensionEnum.className]: FeatureEnum.flower,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.blueberry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.flower_closed]: {
    [CubeDimensionEnum.className]: FeatureEnum.flower_closed,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.blueberry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.flower_open]: {
    [CubeDimensionEnum.className]: FeatureEnum.flower_open,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.blueberry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.flower_petalfall]: {
    [CubeDimensionEnum.className]: FeatureEnum.flower_petalfall,
    [CubeDimensionEnum.modelName]: ModelNamesBesidesFeatureEnums.blueberry,
    [CubeDimensionEnum.modelVersion]: '2',
  },
  [FeatureEnum.earlyCluster]: {
    [CubeDimensionEnum.className]: FeatureEnum.earlyCluster,
    [CubeDimensionEnum.modelName]:
      ModelNamesBesidesFeatureEnums.grape_early_mid_season,
    [CubeDimensionEnum.modelVersion]: '1',
  },
  [FeatureEnum.earlyShoot]: {
    [CubeDimensionEnum.className]: FeatureEnum.earlyShoot,
    [CubeDimensionEnum.modelName]:
      ModelNamesBesidesFeatureEnums.grape_early_mid_season,
    [CubeDimensionEnum.modelVersion]: '1',
  },
};

export const imageMaskLayerVisibilityInitial: ImageMaskVisibilityValues = {
  [FeatureEnum.none]: false,
  [FeatureEnum.nodes]: true,
  [FeatureEnum.bud]: true,
  [FeatureEnum.clusters]: true,
  [FeatureEnum.berries]: true,
  [FeatureEnum.inflorescence]: true,
  // FIXME: Hide Trunks in image mask while Trunks not ready for view
  [FeatureEnum.trunks]: false,
  [FeatureEnum.leaf]: true,
  [FeatureEnum.shoot]: true,
  [FeatureEnum.growth]: true,
  [FeatureEnum.noGrowth]: true,
  [FeatureEnum.blueberry]: false,
  [FeatureEnum.blueberry_Blue]: true,
  [FeatureEnum.blueberry_Green]: true,
  [FeatureEnum.blueberry_Purple]: true,
  [FeatureEnum.blueberry_Yellow]: true,
  [FeatureEnum.flower]: true,
  [FeatureEnum.earlyCluster]: true,
  [FeatureEnum.earlyShoot]: true,
  [FeatureEnum.flower_closed]: true,
  [FeatureEnum.flower_open]: true,
  [FeatureEnum.flower_petalfall]: true,
};

// This is a copy of the colors in dark theme to make it easier to access in this file (ex. theme.colors.darkTheme.red)
const darkThemeColors = {
  blue: '#1ba1fe',
  red: '#ff354d',
  green: '#01c53a',
  yellow: '#DFB200',
  orange: '#e97d09',
  purple: '#BC58FD',
  pink: '#C58298',
  teal: '#00B6C2',
};

export const BlueberryColorsMap: { [key: string]: string } = {
  [FeatureEnum.blueberry_Green]: '#68A434',
  [FeatureEnum.blueberry_Yellow]: '#D085B3',
  [FeatureEnum.blueberry_Purple]: '#9250B1',
  [FeatureEnum.blueberry_Blue]: '#295BA8',
  [FeatureEnum.flower]: '#CB9F4A',
  [FeatureEnum.flower_closed]: '#FFEE93',
  [FeatureEnum.flower_open]: '#CB9F4A',
  [FeatureEnum.flower_petalfall]: '#B6734E',
};

export const maskColorsInitial: MaskColors = {
  [FeatureEnum.none]: '',
  [FeatureEnum.nodes]: darkThemeColors.teal,
  [FeatureEnum.bud]: darkThemeColors.pink,
  [FeatureEnum.clusters]: darkThemeColors.purple,
  [FeatureEnum.berries]: '#a0c4ff',
  [FeatureEnum.inflorescence]: darkThemeColors.orange,
  [FeatureEnum.trunks]: '#000000',
  [FeatureEnum.leaf]: darkThemeColors.blue,
  [FeatureEnum.shoot]: darkThemeColors.red,
  [FeatureEnum.growth]: '#9bf6ff',
  [FeatureEnum.noGrowth]: '#ffadad',
  [FeatureEnum.blueberry]: '#80a6ff',
  [FeatureEnum.blueberry_Green]:
    BlueberryColorsMap[FeatureEnum.blueberry_Green],
  [FeatureEnum.blueberry_Yellow]:
    BlueberryColorsMap[FeatureEnum.blueberry_Yellow],
  [FeatureEnum.blueberry_Purple]:
    BlueberryColorsMap[FeatureEnum.blueberry_Purple],
  [FeatureEnum.blueberry_Blue]: BlueberryColorsMap[FeatureEnum.blueberry_Blue],
  [FeatureEnum.flower]: BlueberryColorsMap[FeatureEnum.flower],
  [FeatureEnum.flower_closed]: BlueberryColorsMap[FeatureEnum.flower_closed],
  [FeatureEnum.flower_open]: BlueberryColorsMap[FeatureEnum.flower_open],
  [FeatureEnum.flower_petalfall]:
    BlueberryColorsMap[FeatureEnum.flower_petalfall],
  [FeatureEnum.earlyCluster]: darkThemeColors.green,
  [FeatureEnum.earlyShoot]: darkThemeColors.yellow,
};

// MAKE SURE TO UPDATE THE TRANSLATION FILE WHEN ADDING A NEW FEATURE TOO.  FOLDER: dashboard\src\i18n\en\common.ts

// EVERY ITEM BETWEEN THESE COMMENTS IS REQUIRED TO UPDATE TO ADD A NEW FEATURE *********************************************************************************************************************

export const featureValueOptions = Object.values(FeatureEnum);

export interface RawCubeData {
  [CubeMeasureEnum.detectionFactCountPerMeter]?: number;
  [CubeDimensionEnum.detectionFactRowsideId]?: string;
  [CubeDimensionEnum.rowsideRowNum]?: number;
  [CubeDimensionEnum.rowsideViewDirection]?: string;
}

interface CubeState {
  currChartDimension: ChartDropdownDimensionEnum;
  currTargetValue: string;
  colorsDistributionMergeFactor: number;
  currHighlightedColorBucket: number | null;
  currHiddenColorBuckets: number[];
}

const initialState: CubeState = {
  currChartDimension: ChartDropdownDimensionEnum.perRowside,
  currTargetValue: '0',
  colorsDistributionMergeFactor: 30,
  currHighlightedColorBucket: null,
  currHiddenColorBuckets: [],
};

export const cubeState = createSlice({
  name: 'cubeState',
  initialState,
  reducers: {
    initCubeState: () => initialState,
    setCurrChartDimension: (
      state,
      action: PayloadAction<ChartDropdownDimensionEnum>
    ) => {
      state.currChartDimension = action.payload;
    },
    setCurrTargetValue: (state, action: PayloadAction<string>) => {
      state.currTargetValue = action.payload;
    },
    setColorsDistributionMergeFactor: (
      state,
      action: PayloadAction<number>
    ) => {
      state.colorsDistributionMergeFactor = action.payload;
      state.currHighlightedColorBucket = null;
      state.currHiddenColorBuckets = [];
    },
    setCurrHighlightedColorBucket: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.currHighlightedColorBucket = action.payload;
    },
    setCurrHiddenColorBuckets: (state, action: PayloadAction<number[]>) => {
      state.currHiddenColorBuckets = action.payload;
    },
  },
});

export const {
  initCubeState,
  setCurrChartDimension,
  setCurrTargetValue,
  setColorsDistributionMergeFactor,
  setCurrHighlightedColorBucket,
  setCurrHiddenColorBuckets,
} = cubeState.actions;

export const selectCurrChartDimension = (state: RootState) =>
  state.cube.currChartDimension;
export const selectCurrTargetValue = (state: RootState) =>
  state.cube.currTargetValue;
export const selectColorsDistributionMergeFactor = (state: RootState) =>
  state.cube.colorsDistributionMergeFactor;
export const selectCurrHighlightedColorBucket = (state: RootState) =>
  state.cube.currHighlightedColorBucket;
export const selectCurrHiddenColorBuckets = (state: RootState) =>
  state.cube.currHiddenColorBuckets;

export const selectAllCubeState = (state: RootState) => state.cube;

export default cubeState.reducer;
